<template>
  <div class="history-page">
    <div class="page-scroll">
      <div class="filter-section">
        <div class="time-range-row" @click="showDatePicker('start')">
          <van-icon name="clock-o" class="range-icon" />
          <span class="range-label">时间</span>
          <span class="range-value">{{ startTime || '开始日期' }}</span>
          <span class="range-sep">~</span>
          <span class="range-value">{{ endTime || '结束日期' }}</span>
        </div>

        <div class="time-actions">
          <van-button type="primary" block class="query-btn" @click="filterData">
            <van-icon name="search" />
            <span>查询</span>
          </van-button>
          <van-button block class="export-btn" @click="exportExcel" :loading="exportLoading">
            <van-icon name="description" />
            <span>导出</span>
          </van-button>
        </div>
      </div>

      <div class="data-tabs">
        <div
          v-for="item in dataDict"
          :key="item.dataType"
          class="data-tab"
          :class="{ active: dataType === item.dataType }"
          @click="switchDataType(item.dataType)"
        >
          <span class="tab-title">{{ item.title }}</span>
        </div>
      </div>

      <div class="list-section">
        <div class="data-header">
          <div class="header-row" :style="gridStyle">
            <div v-for="(col, idx) in currentHeaders" :key="idx" class="header-item">
              <div class="header-label">{{ col.label }}</div>
              <div v-if="col.unit" class="header-unit">{{ col.unit }}</div>
            </div>
          </div>
        </div>

        <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
          <van-list
            v-model="loading"
            :finished="finished"
            :finished-text="displayData.length === 0 ? '' : '没有更多数据了'"
            @load="loadMore"
            :immediate-check="false"
            class="data-list"
          >
            <div v-if="displayData.length === 0 && finished" class="empty-data">
              <div class="empty-icon">
                <van-icon name="search" size="56" color="#BBB" />
              </div>
              <div class="empty-text">暂无数据</div>
              <div class="empty-subtext">调整筛选条件后重试</div>
            </div>

            <div v-for="(item, index) in displayData" :key="item.id || index" class="data-item" :style="gridStyle">
              <div class="data-cell time-cell">
                <div class="date-part">{{ timeParts(item.monitorTime).date }}</div>
                <div v-if="dataType === 'hour'" class="time-part">{{ timeParts(item.monitorTime).time }}</div>
              </div>
              <div class="data-cell">{{ hasVal(item.waterTemperature) ? item.waterTemperature : '/' }}</div>
              <div class="data-cell">{{ hasVal(item.flow) ? item.flow : '/' }}</div>
              <div class="data-cell">{{ hasVal(item.cumulativeFlow) ? item.cumulativeFlow : '/' }}</div>
              <div class="data-cell" :class="{ 'alarm': isRangeAlarm(item, 'chloride') }">
                {{ displayValue(item, 'chloride') }}
                <span v-if="isRangeAlarm(item, 'chloride')" class="alarm-tag">(超标)</span>
              </div>
              <div class="data-cell" :class="{ 'alarm': isRangeAlarm(item, 'ph') }">
                {{ displayValue(item, 'ph') }}
                <span v-if="isRangeAlarm(item, 'ph')" class="alarm-tag">(超标)</span>
              </div>
            </div>
          </van-list>
        </van-pull-refresh>
      </div>
    </div>

    <van-popup v-model="showDatePickerFlag" position="bottom" round :style="{ height: '52%' }">
      <div class="date-picker">
        <div class="date-picker-header">
          <span class="date-picker-cancel" @click="showDatePickerFlag = false">取消</span>
          <span class="date-picker-title">选择时间范围</span>
          <span class="date-picker-confirm" @click="confirmDateRange">确认</span>
        </div>
        <div class="date-picker-tabs">
          <div class="date-tab" :class="{ active: datePickerTab === 'start' }" @click="switchDateTab('start')">
            开始时间
          </div>
          <div class="date-tab" :class="{ active: datePickerTab === 'end' }" @click="switchDateTab('end')">
            结束时间
          </div>
        </div>
        <van-datetime-picker
          v-if="datePickerReady"
          v-model="currentPickerDate"
          type="date"
          title=""
          :show-toolbar="false"
          class="date-picker-wheel"
        />
      </div>
    </van-popup>

    <tabular :active="2" />
  </div>
</template>

<script>
import $ from '@/fly'
import Tabular from '@/components/tabbar'
import request from '@/utils/request'
import { tenantData } from '@/utils/tenantUtils'
import { Dialog, Toast } from 'vant'

export default {
  name: 'historyInfo',
  components: { Tabular },
  data() {
    return {
      loading: false,
      pageNum: 1,
      pageSize: 20,
      startTime: '',
      endTime: '',
      displayData: [],
      finished: false,
      refreshing: false,
      showDatePickerFlag: false,
      currentDatePicker: 'start',
      datePickerTab: 'start',
      datePickerReady: false,
      currentPickerDate: new Date(),
      dataType: 'hour',
      exportLoading: false,
      dataDict: [
        // { title: '分钟数据', dataType: 'minute', headers: ['时间', '温度实时值 ℃', '瞬时流量 L/S', '总余氯实时值mg/L', 'pH实时值'], flex: [1.6, 1, 1, 1.2, 1.2] },
        {
          title: '小时数据',
          dataType: 'hour',
          headers: [{label: '时间'}, {label: '温度时均值', unit: '℃'}, {label: '流量时均值', unit: 'L/S'}, {label: '废水小时排放量', unit: 'm³'}, {label: '总余氯时均值', unit: 'mg/L'}, {label: 'pH时均值'}],
          flex: [1.6, 1, 1.1, 1.2, 1.6, 1.5]
        },
        { title: '日数据',
          dataType: 'day',
          headers: [{label: '时间'}, {label: '温度日均值', unit: '℃'}, {label: '流量日均值', unit: 'L/S'}, {label: '废水每天排放量', unit: 'm³'}, {label: '总余氯日均值', unit: 'mg/L'}, {label: 'pH日均值'}],
          flex: [1.6, 1, 1.1, 1.2, 1.6, 1.5] }
      ]
    }
  },
  computed: {
    currentHeaders() {
      const current = this.dataDict.find(item => item.dataType === this.dataType)
      return current ? current.headers : []
    },
    currentFlex() {
      const current = this.dataDict.find(item => item.dataType === this.dataType)
      return current && current.flex ? current.flex : []
    },
    // 表头与数据行共用同一套栅格列宽，避免两处手写 flex 错位
    gridStyle() {
      const flex = this.currentFlex
      const cols = flex.length
        ? flex.map(f => `minmax(0, ${f}fr)`).join(' ')
        : 'repeat(6, minmax(0, 1fr))'
      return { '--cols': cols }
    }
  },
  mounted() {
    this.setDateRange('7')
    this.$nextTick(() => {
      this.initData()
    })
  },
  methods: {
    timeParts(value) {
      if (!value) return { date: '', time: '' }
      const str = String(value).trim()
      const idx = str.indexOf('T')
      const sep = idx >= 0 ? idx : str.indexOf(' ')
      if (sep < 0) {
        return { date: str, time: '' }
      }
      return {
        date: str.slice(0, sep).trim(),
        time: str.slice(sep + 1, sep + 3).trim() + "时"
      }
    },
    hasVal(value) {
      return value !== null && value !== undefined && value !== ''
    },
    // 非日数据时，流量为 0 或缺失则该指标不参与展示/告警
    isFlowValid(item) {
      return this.hasVal(item.flow) && Number(item.flow) !== 0
    },
    isRangeAlarm(item, field) {
      const v = item[field]
      if (!this.hasVal(v)) return false
      if (this.dataType !== 'day' && !this.isFlowValid(item)) return false
      const [min, max] = field === 'chloride' ? [2.0, 8.0] : [6.0, 9.0]
      const n = Number(v)
      return n < min || n > max
    },
    displayValue(item, field) {
      const v = item[field]
      if (!this.hasVal(v)) return '/'
      if (this.dataType !== 'day' && !this.isFlowValid(item)) return '/'
      return Number(v).toFixed(2)
    },
    setDateRange(days) {
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - parseInt(days))
      const formatDate = (date) => {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }
      this.startTime = formatDate(startDate)
      this.endTime = formatDate(endDate)
    },
    showDatePicker(type) {
      this.currentDatePicker = type
      this.datePickerTab = type
      const savedStr = type === 'start' ? this.startTime : this.endTime
      if (savedStr) {
        const parts = savedStr.split('-')
        this.currentPickerDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
      } else {
        this.currentPickerDate = new Date()
      }
      this.datePickerReady = false
      this.showDatePickerFlag = true
      this.$nextTick(() => {
        this.datePickerReady = true
      })
    },
    switchDateTab(tab) {
      this.datePickerTab = tab
      const savedStr = tab === 'start' ? this.startTime : this.endTime
      if (savedStr) {
        const parts = savedStr.split('-')
        this.currentPickerDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
      } else {
        this.currentPickerDate = new Date()
      }
    },
    confirmDateRange() {
      const value = this.currentPickerDate
      const y = value.getFullYear()
      const m = String(value.getMonth() + 1).padStart(2, '0')
      const d = String(value.getDate()).padStart(2, '0')
      const formatted = `${y}-${m}-${d}`

      if (this.datePickerTab === 'start') {
        if (this.endTime && new Date(formatted) > new Date(this.endTime)) {
          Dialog.alert({ title: '提示', message: '开始时间不能大于结束时间' })
          return
        }
        this.startTime = formatted
        this.datePickerTab = 'end'
        const endStr = this.endTime
        if (endStr) {
          const parts = endStr.split('-')
          this.currentPickerDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
        } else {
          this.currentPickerDate = new Date()
        }
      } else {
        if (this.startTime && new Date(formatted) < new Date(this.startTime)) {
          Dialog.alert({ title: '提示', message: '结束时间不能小于开始时间' })
          return
        }
        this.endTime = formatted
        this.showDatePickerFlag = false
      }
    },
    initData() {
      this.getHistoryData()
    },
    switchDataType(type) {
      if (this.dataType === type) return
      this.dataType = type
      this.pageNum = 1
      this.finished = false
      this.displayData = []
      this.$nextTick(() => {
        this.finished = true
        this.getHistoryData()
      })
    },
    filterData() {
      this.pageNum = 1
      this.finished = false
      this.displayData = []
      this.$nextTick(() => {
        this.finished = true
        this.getHistoryData()
      })
    },
    getHistoryData() {
      this.loading = true
      $.get('mobile/history/historyData', {
        pageNum: this.pageNum,
        pageSize: this.pageSize,
        startTime: this.startTime,
        endTime: this.endTime,
        dataType: this.dataType,
        equCode: tenantData.getEquCode()
      }).then(res => {
        const data = res.data || []
        if (this.pageNum === 1) {
          this.displayData = [...data]
        } else {
          this.displayData = [...this.displayData, ...data]
        }
        this.finished = data.length < this.pageSize
        if (!this.finished) {
          this.pageNum++
        }
      }).catch(() => {
        this.finished = true
      }).finally(() => {
        this.loading = false
        this.refreshing = false
      })
    },
    loadMore() {
      if (!this.finished && !this.refreshing) {
        this.getHistoryData()
      } else {
        this.loading = false
      }
    },
    onRefresh() {
      this.loading = false
      this.pageNum = 1
      this.finished = false
      this.displayData = []
      this.getHistoryData()
    },
    exportExcel() {
      if (this.exportLoading) return
      if (!this.startTime || !this.endTime) {
        Toast('请先选择时间范围')
        return
      }
      this.exportLoading = true
      request.export(
        'mobile/history/exportData',
        {
          startTime: this.startTime + ' 00:00:00',
          endTime: this.endTime + ' 23:59:59',
          equCode: tenantData.getEquCode()
        },
        '监测数据（' + this.startTime + ' 至 ' + this.endTime + '）.xlsx'
      ).finally(() => {
        this.exportLoading = false
      })
    }
  }
}
</script>

<style lang="less" scoped>
/* 页面占满视口，筛选区与表头固定，数据区内部滚动 */
.history-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #F5F7FA;
}

.page-scroll {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px;
}

.filter-section {
  flex-shrink: 0;
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.time-range-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 22px 20px;
  background: #F8FAFC;
  border-radius: 12px;
  cursor: pointer;
  min-height: 80px;
  box-sizing: border-box;

  &:active {
    background: #F0F2F5;
  }
}

.range-icon {
  color: #1B75F6;
  font-size: 32px;
  flex-shrink: 0;
}

.range-label {
  font-size: 28px;
  color: #1a365d;
  font-weight: 600;
  flex-shrink: 0;
  min-width: 72px;
}

.range-value {
  flex: 1;
  font-size: 28px;
  color: #303133;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.range-sep {
  font-size: 26px;
  color: #909399;
  flex-shrink: 0;
}

.time-actions {
  display: flex;
  gap: 12px;
  padding: 4px 0 0;
}

.query-btn {
  flex: 1;
  height: 72px;
  border-radius: 36px;
  font-size: 30px;
  font-weight: 600;
  background: linear-gradient(135deg, #1B75F6 0%, #4A90E2 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(27, 117, 246, 0.3);

  .van-icon {
    margin-right: 8px;
  }
}

.export-btn {
  flex: 1;
  height: 72px;
  border-radius: 36px;
  font-size: 30px;
  font-weight: 600;
  background: linear-gradient(135deg, #52C41A 0%, #73D13D 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(82, 196, 26, 0.3);
  color: #fff;

  .van-icon {
    margin-right: 8px;
  }
}

.date-picker {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

.date-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px 16px;
  border-bottom: 1px solid #F0F2F5;
}

.date-picker-cancel {
  font-size: 30px;
  color: #64748B;
  cursor: pointer;
}

.date-picker-title {
  font-size: 32px;
  font-weight: 600;
  color: #1a365d;
}

.date-picker-confirm {
  font-size: 30px;
  color: #1B75F6;
  font-weight: 600;
  cursor: pointer;
}

.date-picker-tabs {
  display: flex;
  padding: 0 32px;
  border-bottom: 1px solid #F0F2F5;
}

.date-tab {
  flex: 1;
  text-align: center;
  padding: 24px 0;
  font-size: 28px;
  color: #64748B;
  position: relative;
  cursor: pointer;
  transition: all 0.2s;

  &.active {
    color: #1B75F6;
    font-weight: 600;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 48px;
      height: 4px;
      background: linear-gradient(135deg, #1B75F6 0%, #4A90E2 100%);
      border-radius: 2px;
    }
  }
}

.date-picker-wheel {
  flex: 1;

  ::v-deep .van-picker__column {
    flex: 1;
  }

  ::v-deep .van-picker-column__item .van-ellipsis {
    color: #303133;
  }

  ::v-deep .van-picker-column__item--selected .van-ellipsis {
    color: #1B75F6;
    font-weight: 600;
  }
}

.data-tabs {
  display: flex;
  background: #fff;
  border-radius: 14px;
  padding: 6px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
}

.data-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 0;
  font-size: 28px;
  color: #64748B;
  border-radius: 10px;
  transition: all 0.3s;
  cursor: pointer;

  &.active {
    background: linear-gradient(135deg, #1B75F6 0%, #4A90E2 100%);
    color: #fff;
    font-weight: 600;
  }

  .tab-title {
    font-size: 28px;
  }
}

.list-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  ::v-deep .van-pull-refresh {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  ::v-deep .van-pull-refresh__track {
    flex: 1;
    min-height: 0;
  }
}

.data-header {
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  background: linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 100%);
  border-bottom: 1px solid #CBD5E1;
  border-radius: 12px 12px 0 0;
  overflow: hidden;

  /* 列宽由 gridStyle 写入 --cols，与数据行完全一致 */
  .header-row {
    display: grid;
    grid-template-columns: var(--cols, repeat(6, minmax(0, 1fr)));
    padding: 16px 0;
  }

  .header-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 24px;
    font-weight: 600;
    color: #475569;
    padding: 0 6px;
    line-height: 1.3;
    position: relative;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 20%;
      height: 60%;
      width: 1px;
      background: #CBD5E1;
    }
  }

  .header-label {
    width: 100%;
    word-break: break-all;
    text-align: center;
  }

  .header-unit {
    margin-top: 6px;
    font-size: 20px;
    font-weight: 500;
    color: #64748B;
    background: #ffffff;
    border-radius: 8px;
    padding: 2px 8px;
    white-space: nowrap;
  }
}

/* 数据区高度由 flex 分配，不再用 JS 测量高度 */
.data-list {
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar {
    width: 0;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.data-item {
  display: grid;
  grid-template-columns: var(--cols, repeat(6, minmax(0, 1fr)));
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  min-height: 96px;
  padding: 14px 0;
  border-bottom: 1px solid #F1F5F9;
  font-size: 26px;
  color: #334155;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: #F1F5F9;
  }

  .data-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 6px;
    line-height: 1.4;
    word-break: break-all;
    color: #334155;
    font-variant-numeric: tabular-nums;
  }

  .time-cell {
    flex-direction: column;
    color: #334155;
    font-size: 22px;

    .date-part {
      font-weight: 500;
    }

    .time-part {
      margin-top: 4px;
    }
  }

  .alarm {
    color: #EF4444;
    font-weight: 500;
  }

  .alarm-tag {
    font-size: 18px;
    color: #EF4444;
    margin-left: 2px;
    font-weight: 500;
  }
}

.empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;

  .empty-icon {
    margin-bottom: 16px;
  }

  .empty-text {
    font-size: 30px;
    font-weight: 600;
    color: #64748B;
    margin-bottom: 8px;
  }

  .empty-subtext {
    font-size: 26px;
    color: #909399;
  }
}
</style>
